import { useParams, Link, Navigate } from "react-router-dom";
import { useState } from "react";
import { ArrowLeft, Scissors } from "lucide-react";
import FlowerButton from "@/components/FlowerButton";
import { Reviews } from "@/components/Reviews";
import { StockBadge, useStockInfo } from "@/components/StockBadge";
import { useCart } from "@/context/CartContext";
import { findProduct, findCharacter, fmtPrice } from "@/data/mellow";

const Line = ({ label, value, testId }) => (
  <div className="flex items-baseline gap-2 text-sm text-[#4A3B32] min-w-0" data-testid={testId}>
    <span className="min-w-0 break-words">{label}</span>
    <span className="flex-1 min-w-[14px] border-b-2 border-dotted border-[#4A3B32]/35 translate-y-[-4px]" aria-hidden="true" />
    <span className="shrink-0 font-semibold">{value}</span>
  </div>
);

const Section = ({ title, children, testId }) => (
  <section className="mt-7" data-testid={testId}>
    <h2 className="font-cursive text-3xl text-[#78353A] leading-none">{title}</h2>
    <div className="mt-2 text-sm leading-relaxed text-[#4A3B32]/90">{children}</div>
  </section>
);

const Gallery = ({ product }) => {
  const images = product.gallery || [{ src: product.img, alt: product.name }];
  const [active, setActive] = useState(0);
  return (
    <div>
      <img key={images[active].src} src={images[active].src} alt={images[active].alt} className="w-full rounded-[1.75rem] shadow-[0_20px_60px_rgb(0,0,0,0.10)] object-cover" data-testid="product-main-image" />
      {images.length > 1 && (
        <div className="mt-4 flex flex-wrap gap-3" data-testid="product-gallery">
          {images.map((im, i) => (
            <button key={im.src} type="button" onClick={() => setActive(i)} aria-label={im.alt} aria-pressed={i === active}
              className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border-2 transition-all duration-200 ${i === active ? "border-[#78353A] scale-105" : "border-dashed border-[#D1D5CB] opacity-70 hover:opacity-100"}`}
              data-testid={`product-thumb-${i}`}>
              <img src={im.src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Product() {
  const { key } = useParams();
  const product = findProduct(key);
  const { add } = useCart();
  const { soldOut } = useStockInfo(key);
  if (!product) return <Navigate to="/boutique" replace />;
  const char = product.character ? findCharacter(product.character) : null;
  const included = (product.includes || []).map(findProduct).filter(Boolean);
  const today = new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="product-page">
      <Link to={`/boutique/${product.category}`} className="inline-flex items-center gap-2 text-sm text-[#78353A] hover:underline underline-offset-4 decoration-dotted" data-testid="product-back-link">
        <ArrowLeft className="w-4 h-4" /> Retour à la boutique
      </Link>

      <div className="mt-6 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,460px)] gap-10 items-start">
        <Gallery product={product} />

        {/* Receipt */}
        <div className="receipt relative bg-[#FFFDF9] px-7 sm:px-9 pt-9 pb-12 shadow-[0_18px_50px_rgb(74,59,50,0.12)]" data-testid="product-receipt">
          <div className="text-center">
            <img src="/logo-minimellow.png" alt="MiniMellow" className="h-12 mx-auto" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <p className="mt-2 font-cursive text-2xl text-[#78353A]/80">Reçu</p>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#4A3B32]/55 mt-1">MellowCity · {today}</p>
          </div>

          <div className="my-5 border-t-2 border-dashed border-[#D1D5CB]" />

          {product.isNew && <span className="inline-block mb-2 font-cursive text-lg px-3 py-0.5 rounded-full bg-[#FFF2B2] text-[#78353A]" data-testid="product-new-badge">Nouveau ✿</span>}
          <h1 className="font-cursive text-4xl sm:text-5xl text-[#78353A] leading-none" data-testid="product-title">{product.name}</h1>
          {char && (
            <Link to={`/personnages/${char.slug}`} className="mt-2 inline-block text-xs text-[#4A3B32]/70 underline decoration-dotted underline-offset-4" data-testid="product-character-link">
              Collection {char.name} {char.surname}
            </Link>
          )}

          <div className="mt-5 space-y-1.5">
            {included.length > 0
              ? included.map((p) => <Line key={p.lookup_key} label={`1 × ${p.name}`} value={fmtPrice(p.price)} testId={`receipt-line-${p.lookup_key}`} />)
              : <Line label={`1 × ${product.name}`} value={fmtPrice(product.price)} testId="receipt-line-product" />}
            {product.compareAt && <Line label="Petit prix doux" value={`− ${fmtPrice(product.compareAt - product.price)}`} testId="receipt-line-discount" />}
            <div className="pt-2 flex items-baseline justify-between font-cursive text-3xl text-[#78353A]">
              <span>Total</span>
              <span data-testid="product-price">{fmtPrice(product.price)}{product.compareAt && <span className="ml-2 text-lg text-[#4A3B32]/50 line-through">{fmtPrice(product.compareAt)}</span>}</span>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <FlowerButton variant="pink" onClick={() => add(product.lookup_key)} disabled={soldOut} data-testid={`add-to-cart-${product.lookup_key}`}>
              {soldOut ? "Épuisé" : "Ajouter au panier"}
            </FlowerButton>
            <StockBadge lookup_key={product.lookup_key} />
            {product.lookup_key === "green-days-carnet" && (
              <Link to="/feuilleter" className="font-cursive text-xl text-[#78353A] underline decoration-dotted underline-offset-4" data-testid="product-feuilleter-link">Feuilleter le carnet</Link>
            )}
          </div>

          <Section title="Description" testId="product-description"><p>{product.desc}</p></Section>

          {product.details && (
            <Section title="Dans le détail" testId="product-details">
              <ul className="list-disc pl-5 space-y-0.5">{product.details.map((d) => <li key={d}>{d}</li>)}</ul>
            </Section>
          )}

          {product.illustrationStory && (
            <Section title="L'histoire de l'illustration" testId="product-story"><p>{product.illustrationStory}</p></Section>
          )}

          {product.uses && (
            <Section title="Comment l'utiliser" testId="product-uses">
              <ul className="space-y-1">{product.uses.map((u) => <li key={u} className="flex gap-2"><span className="text-[#78353A]">✿</span><span>{u}</span></li>)}</ul>
            </Section>
          )}

          {product.themes && (
            <Section title="Les 8 thèmes du carnet" testId="product-themes">
              <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1">{product.themes.map((t, i) => <li key={t.title} className="flex gap-2"><span className="font-cursive text-lg text-[#78353A]">{i + 1}.</span><span>{t.title}</span></li>)}</ol>
            </Section>
          )}

          <div className="mt-8 text-center">
            <div className="border-t-2 border-dashed border-[#D1D5CB] mb-4" />
            <p className="font-cursive text-2xl text-[#78353A]/80">Merci et à bientôt à MellowCity ✿</p>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#4A3B32]/55 mt-1 inline-flex items-center gap-1"><Scissors className="w-3 h-3" /> mini-mellow.com</p>
          </div>
        </div>
      </div>

      <Reviews productKey={product.lookup_key} productName={product.name} />
    </div>
  );
}
